import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CalendarX, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CancelPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("This cancel link is missing its reference. Please call 07823 945998 instead.");
      return;
    }
    axios
      .get(`${API}/enquiries/cancel/${token}`)
      .then((r) => {
        setBooking(r.data);
        if (r.data.status === "cancelled") setDone(true);
      })
      .catch(() => setError("We couldn't find this booking — it may have already been removed."));
  }, [token]);

  const cancel = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/enquiries/cancel`, { token });
      setDone(true);
    } catch {
      setError("Something went wrong — please call 07823 945998 to cancel.");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF8F5] px-6">
      <motion.div
        data-testid="cancel-page"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <div className="mb-8 text-center">
          <p className="font-serif text-3xl font-medium text-[#0F382A]">
            Don Sparkle<span className="text-[#D4AF37]">.</span>
          </p>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#4A5A52]">
            Manage your booking
          </p>
        </div>

        <div className="rounded-[1.75rem] border border-[#E5DEC3] bg-white p-8 shadow-[0_40px_80px_-40px_rgba(11,29,22,0.18)]">
          {error && (
            <p data-testid="cancel-error" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
          )}

          {!error && !booking && (
            <p className="text-center font-mono text-xs uppercase tracking-[0.25em] text-[#4A5A52]">Loading…</p>
          )}

          {booking && !done && (
            <>
              <p className="font-serif text-2xl font-light text-[#121A16]">
                Hi {booking.name.split(" ")[0]}, your booking request
              </p>
              <div className="mt-5 space-y-2 rounded-xl bg-[#FAF8F5] p-5 text-sm text-[#4A5A52]">
                <p><span className="font-semibold text-[#0F382A]">Service:</span> {booking.service}</p>
                {booking.preferred_date && (
                  <p><span className="font-semibold text-[#0F382A]">Date:</span> {booking.preferred_date}</p>
                )}
                {booking.slot_label && (
                  <p><span className="font-semibold text-[#0F382A]">Time:</span> {booking.slot_label}</p>
                )}
              </div>
              <button
                data-testid="cancel-confirm-button"
                onClick={cancel}
                disabled={cancelling}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#0B1D16] py-3.5 text-sm font-semibold text-[#FAF8F5] transition-[background-color,opacity] duration-300 hover:bg-[#1B523F] disabled:opacity-60"
              >
                <CalendarX className="h-4 w-4" strokeWidth={1.75} />
                {cancelling ? "Cancelling…" : "Cancel this booking"}
              </button>
              <p className="mt-3 text-center text-xs text-[#4A5A52]">
                Changed your mind? Just close this page — nothing happens until you tap the button.
              </p>
            </>
          )}

          {done && (
            <div data-testid="cancel-success" className="text-center">
              <CheckCircle2 className="mx-auto h-12 w-12 text-[#0F382A]" strokeWidth={1.5} />
              <p className="mt-4 font-serif text-2xl font-light text-[#121A16]">All cancelled.</p>
              <p className="mt-2 text-sm leading-relaxed text-[#4A5A52]">
                Your slot has been freed up automatically and Don has been
                notified. We'd love to see you another time.
              </p>
              <a
                data-testid="cancel-rebook-link"
                href="/#booking"
                className="mt-6 inline-block rounded-full bg-[#0F382A] px-8 py-3 text-sm font-semibold text-[#FAF8F5] transition-colors duration-300 hover:bg-[#1B523F]"
              >
                Book a new slot
              </a>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-[#4A5A52]">
          <a href="/" className="underline decoration-[#D4AF37] underline-offset-4 hover:text-[#0F382A]">
            Back to website
          </a>
        </p>
      </motion.div>
    </div>
  );
}
